/* =========================================================
   BlogAdmin.jsx
   ---------------------------------------------------------
   Rich text editor: Quill (react-quill)
   Styling: 100% Tailwind + inline Quill dark overrides
   Optimised for bulk copy/paste from Microsoft Word,
   Google Docs and web pages.
========================================================= */

import React, {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";

/* Quill's base CSS (required — this is not optional) */
import "react-quill/dist/quill.snow.css";
import {
  BookOpen,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  X,
  ArrowUp,
  ArrowDown,
  Image as ImageIcon,
} from "lucide-react";

import {
  getBlogs,
  createBlog,
  updateBlog,
  deleteBlog,
  moveBlogUp,
  moveBlogDown,
} from "./api/blogApi";

/* =========================================================
   LAZY LOAD QUILL  (SSR / Vite safe)
========================================================= */

const ReactQuill = lazy(() =>
  import("react-quill").then((mod) => ({
    default: mod?.default?.default ?? mod?.default ?? mod,
  }))
);

/* =========================================================
   QUILL CONFIGURATION
========================================================= */

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["blockquote", "code-block"],
    ["link", "image"],
    ["clean"],
  ],
  clipboard: {
    matchVisual: false,
  },
  history: {
    delay: 500,
    maxStack: 300,
    userOnly: true,
  },
};

const QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "align",
  "list",
  "indent",
  "blockquote",
  "code-block",
  "code",
  "link",
  "image",
];

/* =========================================================
   QUILL DARK THEME (injected once — no external CSS file)
========================================================= */

const QUILL_DARK_STYLES = `
  .blog-quill .ql-toolbar.ql-snow {
    border: none;
    border-bottom: 1px solid #3f3f46;
    background: #27272a;
    padding: 10px 12px;
    border-top-left-radius: 0.75rem;
    border-top-right-radius: 0.75rem;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
  }

  .blog-quill .ql-toolbar.ql-snow .ql-formats {
    margin-right: 8px;
    display: inline-flex;
    align-items: center;
    gap: 2px;
  }

  .blog-quill .ql-toolbar.ql-snow button,
  .blog-quill .ql-toolbar.ql-snow .ql-picker-label {
    color: #a1a1aa;
    border-radius: 6px;
    transition: all 0.15s ease;
  }

  .blog-quill .ql-toolbar.ql-snow button:hover,
  .blog-quill .ql-toolbar.ql-snow .ql-picker-label:hover {
    background: #3f3f46;
    color: #ffffff;
  }

  .blog-quill .ql-toolbar.ql-snow button.ql-active,
  .blog-quill .ql-toolbar.ql-snow .ql-picker-label.ql-active,
  .blog-quill .ql-toolbar.ql-snow .ql-picker-item.ql-selected {
    color: #818cf8;
    background: rgba(99, 102, 241, 0.18);
  }

  .blog-quill .ql-toolbar.ql-snow button.ql-active .ql-stroke,
  .blog-quill .ql-toolbar.ql-snow .ql-picker-label.ql-active .ql-stroke {
    stroke: #818cf8;
  }

  .blog-quill .ql-toolbar.ql-snow button.ql-active .ql-fill,
  .blog-quill .ql-toolbar.ql-snow .ql-picker-label.ql-active .ql-fill {
    fill: #818cf8;
  }

  .blog-quill .ql-toolbar.ql-snow .ql-stroke {
    stroke: #a1a1aa;
  }

  .blog-quill .ql-toolbar.ql-snow .ql-fill {
    fill: #a1a1aa;
  }

  .blog-quill .ql-toolbar.ql-snow .ql-picker {
    color: #a1a1aa;
  }

  .blog-quill .ql-toolbar.ql-snow .ql-picker-options {
    background: #18181b;
    border: 1px solid #3f3f46;
    border-radius: 8px;
    padding: 6px;
  }

  .blog-quill .ql-toolbar.ql-snow .ql-picker-item {
    color: #d4d4d8;
    border-radius: 4px;
    padding: 4px 8px;
  }

  .blog-quill .ql-toolbar.ql-snow .ql-picker-item:hover {
    background: #3f3f46;
    color: #ffffff;
  }

  .blog-quill .ql-container.ql-snow {
    border: none;
    background: #18181b;
    border-bottom-left-radius: 0.75rem;
    border-bottom-right-radius: 0.75rem;
    font-family: inherit;
    font-size: 15px;
  }

  .blog-quill .ql-editor {
    min-height: 320px;
    padding: 20px;
    color: #e4e4e7;
    line-height: 1.7;
    font-size: 15px;
  }

  .blog-quill .ql-editor.ql-blank::before {
    color: #71717a;
    font-style: normal;
    left: 20px;
    right: 20px;
  }

  .blog-quill .ql-editor h1,
  .blog-quill .ql-editor h2,
  .blog-quill .ql-editor h3,
  .blog-quill .ql-editor h4,
  .blog-quill .ql-editor h5,
  .blog-quill .ql-editor h6 {
    color: #ffffff;
    font-weight: 600;
    margin-top: 1em;
    margin-bottom: 0.5em;
  }

  .blog-quill .ql-editor p {
    margin-bottom: 0.75em;
  }

  .blog-quill .ql-editor a {
    color: #818cf8;
    text-decoration: underline;
  }

  .blog-quill .ql-editor blockquote {
    border-left: 4px solid #6366f1;
    padding-left: 1em;
    color: #a1a1aa;
    margin: 1em 0;
    font-style: italic;
  }

  .blog-quill .ql-editor pre,
  .blog-quill .ql-editor .ql-code-block-container {
    background: #09090b;
    color: #e4e4e7;
    border-radius: 8px;
    padding: 12px 16px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
    overflow-x: auto;
  }

  .blog-quill .ql-editor code {
    background: #27272a;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
    color: #f0abfc;
  }

  .blog-quill .ql-editor img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 0.5em 0;
    cursor: pointer;
  }

  .blog-quill .ql-editor img.ql-image-selected {
    outline: 2px solid #6366f1;
    outline-offset: 2px;
  }

  .blog-quill .ql-image-resizer {
    position: absolute;
    z-index: 20;
    pointer-events: none;
    box-sizing: border-box;
    border: 1px dashed #818cf8;
  }

  .blog-quill .ql-image-resizer .ql-image-handle {
    position: absolute;
    width: 10px;
    height: 10px;
    border: 1px solid #ffffff;
    border-radius: 2px;
    background: #6366f1;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
    pointer-events: auto;
    cursor: nwse-resize;
    box-sizing: border-box;
  }

  .blog-quill .ql-image-resizer .ql-image-handle.n,
  .blog-quill .ql-image-resizer .ql-image-handle.s {
    left: 50%;
    transform: translateX(-50%);
    cursor: ns-resize;
  }

  .blog-quill .ql-image-resizer .ql-image-handle.e,
  .blog-quill .ql-image-resizer .ql-image-handle.w {
    top: 50%;
    transform: translateY(-50%);
    cursor: ew-resize;
  }

  .blog-quill .ql-image-resizer .ql-image-handle.n { top: -5px; }
  .blog-quill .ql-image-resizer .ql-image-handle.s { bottom: -5px; }
  .blog-quill .ql-image-resizer .ql-image-handle.e { right: -5px; }
  .blog-quill .ql-image-resizer .ql-image-handle.w { left: -5px; }

  .blog-quill .ql-image-resizer .ql-image-handle.nw { top: -5px; left: -5px; }
  .blog-quill .ql-image-resizer .ql-image-handle.ne { top: -5px; right: -5px; }
  .blog-quill .ql-image-resizer .ql-image-handle.sw { bottom: -5px; left: -5px; }
  .blog-quill .ql-image-resizer .ql-image-handle.se { bottom: -5px; right: -5px; }

  .blog-quill .ql-image-context-menu {
    position: absolute;
    z-index: 50;
    min-width: 150px;
    padding: 6px;
    border: 1px solid #3f3f46;
    border-radius: 8px;
    background: #18181b;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.35);
  }

  .blog-quill .ql-image-context-menu button {
    display: block;
    width: 100%;
    padding: 7px 10px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #d4d4d8;
    text-align: left;
    font-size: 12px;
    cursor: pointer;
  }

  .blog-quill .ql-image-context-menu button:hover {
    background: #3f3f46;
    color: #ffffff;
  }

  .blog-quill .ql-image-size-label {
    position: absolute;
    left: 50%;
    bottom: -28px;
    transform: translateX(-50%);
    padding: 3px 7px;
    border-radius: 5px;
    background: #27272a;
    color: #e4e4e7;
    border: 1px solid #3f3f46;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    pointer-events: none;
  }

  .blog-quill .ql-editor ul,
  .blog-quill .ql-editor ol {
    padding-left: 1.5em;
    margin-bottom: 0.75em;
  }

  .blog-quill .ql-editor li {
    margin-bottom: 0.25em;
  }

  .blog-quill .ql-editor hr {
    border-color: #3f3f46;
    margin: 1.25em 0;
  }

  .blog-quill .ql-editor.ql-blank::before {
    content: attr(data-placeholder);
  }

  /* Scrollbar */
  .blog-quill .ql-editor::-webkit-scrollbar {
    width: 8px;
  }
  .blog-quill .ql-editor::-webkit-scrollbar-track {
    background: #18181b;
  }
  .blog-quill .ql-editor::-webkit-scrollbar-thumb {
    background: #3f3f46;
    border-radius: 4px;
  }
  .blog-quill .ql-editor::-webkit-scrollbar-thumb:hover {
    background: #52525b;
  }
`;

/* =========================================================
   MICROSOFT WORD / OFFICE HTML CLEAN-UP UTILITIES
========================================================= */

const JUNK_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  "META",
  "LINK",
  "TITLE",
  "HEAD",
  "BASE",
  "IFRAME",
  "OBJECT",
  "EMBED",
  "APPLET",
  "FORM",
  "INPUT",
  "BUTTON",
  "TEXTAREA",
  "SELECT",
  "OPTION",
  "NOSCRIPT",
  "XML",
  "FRAME",
  "FRAMESET",
  "COLGROUP",
  "COL",
]);

const ALLOWED_STYLE_PROPS = new Set([
  "color",
  "background-color",
  "text-align",
  "text-decoration",
  "text-decoration-line",
  "font-weight",
  "font-style",
]);

const SAFE_TEXT_ALIGN = new Set(["left", "right", "center", "justify"]);

const BULLET_GLYPHS =
  /^[\s\u00a0]*[\u2022\u25cf\u25aa\u25e6\u2043\u00b7\uf0b7\uf0a7\u25a0\u25ab]\s*/;

function parseStyleAttribute(styleText) {
  const result = {};

  if (!styleText || typeof styleText !== "string") return result;

  styleText.split(";").forEach((declaration) => {
    const separator = declaration.indexOf(":");
    if (separator === -1) return;

    let property = declaration.slice(0, separator).trim().toLowerCase();
    let value = declaration.slice(separator + 1).trim();

    if (!property || !value) return;

    if (property === "mso-highlight") property = "background-color";
    if (property === "background") property = "background-color";

    if (property.startsWith("mso")) return;
    if (property.startsWith("-")) return;

    if (!ALLOWED_STYLE_PROPS.has(property)) return;

    value = value.replace(/\s*!important\s*$/i, "").trim();
    if (!value) return;

    const lower = value.toLowerCase();

    if (property === "text-align") {
      if (!SAFE_TEXT_ALIGN.has(lower)) return;
      result["text-align"] = lower;
      return;
    }

    if (
      property === "text-decoration" ||
      property === "text-decoration-line"
    ) {
      const parts = lower
        .split(/\s+/)
        .filter((p) => p === "underline" || p === "line-through");

      if (!parts.length) return;

      result["text-decoration"] = parts.join(" ");
      return;
    }

    if (property === "font-weight") {
      if (!/^(normal|bold|bolder|lighter|[1-9]00)$/.test(lower)) return;
      result["font-weight"] = lower;
      return;
    }

    if (property === "font-style") {
      if (lower !== "italic" && lower !== "normal") return;
      result["font-style"] = lower;
      return;
    }

    if (property === "color" || property === "background-color") {
      if (lower === "windowtext" || lower === "windowframe") return;

      if (
        !/^(#[0-9a-f]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\)|[a-z]+)$/.test(lower)
      ) {
        return;
      }

      result[property] = lower;
      return;
    }

    result[property] = value;
  });

  return result;
}

function removeComments(root) {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_COMMENT, null);

  const comments = [];
  while (walker.nextNode()) comments.push(walker.currentNode);

  comments.forEach((node) => {
    if (node.parentNode) node.parentNode.removeChild(node);
  });
}

function removeJunkElements(root) {
  Array.from(root.querySelectorAll("*")).forEach((el) => {
    const tag = el.tagName.toUpperCase();

    if (tag.indexOf(":") !== -1) {
      el.remove();
      return;
    }

    if (JUNK_TAGS.has(tag)) {
      el.remove();
    }
  });
}

function stripLeadingBullet(li) {
  const doc = li.ownerDocument;
  const walker = doc.createTreeWalker(li, NodeFilter.SHOW_TEXT, null);
  const textNode = walker.nextNode();

  if (!textNode) return;

  const value = textNode.nodeValue || "";
  const cleaned = value.replace(BULLET_GLYPHS, "");

  if (cleaned !== value) textNode.nodeValue = cleaned;
}

function convertWordLists(root) {
  const doc = root.ownerDocument;

  const candidates = Array.from(
    root.querySelectorAll("p, div, li, h1, h2, h3, h4, h5, h6")
  ).filter((el) => /mso-list\s*:/i.test(el.getAttribute("style") || ""));

  candidates.forEach((el) => {
    if (!el.parentNode) return;

    const style = el.getAttribute("style") || "";

    const levelMatch = style.match(/level\s*(\d+)/i);
    const level = levelMatch ? parseInt(levelMatch[1], 10) : 1;

    let markerText = "";

    Array.from(el.querySelectorAll("*")).forEach((child) => {
      const childStyle = child.getAttribute("style") || "";

      if (/mso-list\s*:\s*ignore/i.test(childStyle)) {
        if (!markerText) {
          markerText = (child.textContent || "").trim();
        }
        child.remove();
      }
    });

    const ordered = /^\s*(\d{1,3}|[a-zA-Z])[.)]/.test(markerText);

    const li = doc.createElement("li");

    while (el.firstChild) li.appendChild(el.firstChild);

    if (level > 1) {
      li.setAttribute("class", `ql-indent-${Math.min(level - 1, 8)}`);
    }

    li.setAttribute("data-word-list", ordered ? "ordered" : "bullet");

    stripLeadingBullet(li);

    el.parentNode.replaceChild(li, el);
  });
}

function wrapListRuns(root) {
  const doc = root.ownerDocument;

  const containers = [root, ...Array.from(root.querySelectorAll("*"))];

  containers.forEach((container) => {
    if (!container.childNodes || !container.childNodes.length) return;

    let run = [];
    let runType = null;

    const flush = () => {
      if (!run.length) return;

      const list = doc.createElement(runType === "ordered" ? "ol" : "ul");

      container.insertBefore(list, run[0]);
      run.forEach((node) => list.appendChild(node));

      run = [];
      runType = null;
    };

    Array.from(container.childNodes).forEach((node) => {
      const isItem =
        node.nodeType === 1 &&
        node.tagName === "LI" &&
        node.hasAttribute("data-word-list");

      if (isItem) {
        const type = node.getAttribute("data-word-list");

        if (run.length && type !== runType) flush();

        runType = type;
        run.push(node);
        return;
      }

      if (run.length) {
        if (node.nodeType === 3 && !node.nodeValue.trim()) {
          run.push(node);
          return;
        }

        flush();
      }
    });

    flush();
  });
}

function sanitizeElements(root) {
  const elements = Array.from(root.querySelectorAll("*"));

  elements.forEach((el) => {
    const tag = el.tagName;

    const attributes = {};
    Array.from(el.attributes).forEach((attr) => {
      attributes[attr.name.toLowerCase()] = attr.value;
    });

    const keep = {};

    if (tag === "A") {
      const href = attributes.href || "";

      if (href && !/^\s*javascript:/i.test(href)) keep.href = href;
      if (attributes.title) keep.title = attributes.title;
    }

    if (tag === "IMG") {
      const src = attributes.src || "";

      if (!src || /^file:/i.test(src)) {
        el.remove();
        return;
      }

      keep.src = src;
      if (attributes.alt) keep.alt = attributes.alt;
      if (attributes.title) keep.title = attributes.title;
    }

    if (tag === "BR" && attributes.clear) {
      el.remove();
      return;
    }

    if (tag === "OL" && attributes.start) keep.start = attributes.start;

    if (tag === "TD" || tag === "TH") {
      if (attributes.colspan) keep.colspan = attributes.colspan;
      if (attributes.rowspan) keep.rowspan = attributes.rowspan;
    }

    if (attributes.class && /ql-indent-\d/.test(attributes.class)) {
      keep.class = attributes.class.match(/ql-indent-\d/)[0];
    }

    const styles = parseStyleAttribute(attributes.style);

    if (!styles["text-align"] && attributes.align) {
      const align = String(attributes.align).toLowerCase();
      if (SAFE_TEXT_ALIGN.has(align)) styles["text-align"] = align;
    }

    if (tag === "FONT" && attributes.color && !styles.color) {
      const color = String(attributes.color).toLowerCase();
      if (color !== "windowtext" && color !== "windowframe") {
        styles.color = color;
      }
    }

    Array.from(el.attributes).forEach((attr) => el.removeAttribute(attr.name));

    Object.keys(keep).forEach((name) => el.setAttribute(name, keep[name]));

    const styleString = Object.keys(styles)
      .map((property) => `${property}: ${styles[property]}`)
      .join("; ");

    if (styleString) el.setAttribute("style", styleString);
  });
}

function mergeAdjacentLists(root) {
  // Word/Docs HTML can produce one <ol> per numbered paragraph.
  // Merge consecutive ordered lists so Quill renders 1, 2, 3... instead
  // of restarting at 1 for every item.
  const mergeInContainer = (container) => {
    let current = container.firstElementChild;

    while (current) {
      const next = current.nextElementSibling;

      if (
        next &&
        current.tagName === next.tagName &&
        (current.tagName === "OL" || current.tagName === "UL")
      ) {
        while (next.firstChild) {
          current.appendChild(next.firstChild);
        }
        next.remove();
        continue;
      }

      current = next;
    }
  };

  [root, ...Array.from(root.querySelectorAll("*"))].forEach((container) => {
    mergeInContainer(container);
  });
}

function normalizeInlineElements(root) {
  const doc = root.ownerDocument;

  Array.from(root.querySelectorAll("font")).forEach((font) => {
    if (!font.parentNode) return;

    const span = doc.createElement("span");

    Array.from(font.attributes).forEach((attr) => {
      span.setAttribute(attr.name, attr.value);
    });

    while (font.firstChild) span.appendChild(font.firstChild);

    font.parentNode.replaceChild(span, font);
  });

  Array.from(root.querySelectorAll("span")).forEach((span) => {
    if (!span.parentNode) return;

    if (span.attributes.length > 0) return;

    if (!span.firstChild) {
      span.remove();
      return;
    }

    while (span.firstChild) {
      span.parentNode.insertBefore(span.firstChild, span);
    }

    span.remove();
  });
}

function removeEmptyBlocks(root) {
  const blocks = Array.from(
    root.querySelectorAll("p, div, h1, h2, h3, h4, h5, h6")
  );

  blocks.forEach((el) => {
    if (!el.parentNode) return;

    if (el.querySelector("img, table, ul, ol, hr, video, audio, iframe")) {
      return;
    }

    const text = (el.textContent || "").replace(/\u00a0/g, " ").trim();

    if (text) return;

    const onlyBreaks =
      el.children.length === 0 ||
      Array.from(el.children).every((child) => child.tagName === "BR");

    if (onlyBreaks) el.remove();
  });
}

function unwrapEmptyDivs(root) {
  Array.from(root.querySelectorAll("div")).forEach((div) => {
    if (!div.parentNode) return;
    if (div.attributes.length > 0) return;

    while (div.firstChild) {
      div.parentNode.insertBefore(div.firstChild, div);
    }

    div.remove();
  });
}

function cleanWordHtml(rawHtml) {
  if (!rawHtml || typeof rawHtml !== "string") return "";

  let doc;

  try {
    doc = new DOMParser().parseFromString(rawHtml, "text/html");
  } catch (error) {
    console.error("Paste sanitizer failed to parse HTML:", error);
    return rawHtml;
  }

  const body = doc.body;
  if (!body) return "";

  removeComments(body);
  removeJunkElements(body);
  convertWordLists(body);
  wrapListRuns(body);
  sanitizeElements(body);
  mergeAdjacentLists(body);
  normalizeInlineElements(body);
  unwrapEmptyDivs(body);
  removeEmptyBlocks(body);

  return body.innerHTML.trim();
}

/* =========================================================
   CUSTOM QUILL IMAGE RESIZER
   ---------------------------------------------------------
   No third-party resize package required.
   Supports:
   - 8 resize handles
   - aspect-ratio preserving corner resize
   - width/height persistence in HTML
   - left / center / right alignment
   ========================================================= */

const IMAGE_RESIZE_MIN_WIDTH = 40;
const IMAGE_RESIZE_MAX_WIDTH = 2000;

function applyImageAlignment(image, alignment) {
  if (!image) return;

  // Keep the image shrink-to-content so text-align can actually move it.
  // A block-level image would occupy the full line and appear not to move.
  image.style.display = "inline-block";
  image.style.marginLeft = "0";
  image.style.marginRight = "0";

  // Quill image embeds normally live inside a paragraph/block.
  // Align the containing block so left/center/right works reliably.
  const block = image.closest("p, div, h1, h2, h3, h4, h5, h6, li");

  if (block) {
    block.style.textAlign = alignment;
    return;
  }

  // Fallback when the image is not inside a normal Quill block.
  image.style.display = "block";
  image.style.marginLeft =
    alignment === "center" || alignment === "right" ? "auto" : "0";
  image.style.marginRight =
    alignment === "center" ? "auto" : alignment === "right" ? "0" : "auto";
}

const QuillImageResizer = ({ quillRef, wrapperRef, onChange, setSelectedImage, setImageWidth }) => {
  const cleanupRef = useRef(null);

  useEffect(() => {
    const quill = quillRef.current?.getEditor?.();
    const wrapper = wrapperRef.current;

    if (!quill || !wrapper) return undefined;

    const editor = quill.root;
    let selectedImage = null;
    let overlay = null;
    let contextMenu = null;
    let dragState = null;

    const removeOverlay = () => {
      if (selectedImage) {
        selectedImage.classList.remove("ql-image-selected");
      }

      if (overlay?.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }

      removeContextMenu();

      overlay = null;
      selectedImage = null;
      setSelectedImage(null);
      setImageWidth("");
    };

    const updateOverlay = () => {
      if (!selectedImage || !selectedImage.isConnected) {
        removeOverlay();
        return;
      }

      if (!overlay) {
        overlay = document.createElement("div");
        overlay.className = "ql-image-resizer";

        [
          "nw", "n", "ne",
          "e",
          "se", "s", "sw",
          "w",
        ].forEach((position) => {
          const handle = document.createElement("span");
          handle.className = `ql-image-handle ${position}`;
          handle.dataset.position = position;
          overlay.appendChild(handle);
        });

        const label = document.createElement("div");
        label.className = "ql-image-size-label";
        overlay.appendChild(label);

        wrapper.appendChild(overlay);
      }

      const imageRect = selectedImage.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();

      overlay.style.left = `${imageRect.left - wrapperRect.left + wrapper.scrollLeft}px`;
      overlay.style.top = `${imageRect.top - wrapperRect.top + wrapper.scrollTop}px`;
      overlay.style.width = `${imageRect.width}px`;
      overlay.style.height = `${imageRect.height}px`;

      const label = overlay.querySelector(".ql-image-size-label");
      if (label) {
        label.textContent = `${Math.round(imageRect.width)} × ${Math.round(imageRect.height)}`;
      }
    };

    const removeContextMenu = () => {
      if (contextMenu?.parentNode) {
        contextMenu.parentNode.removeChild(contextMenu);
      }
      contextMenu = null;
    };

    const showImageContextMenu = (image, event) => {
      if (!image) return;

      removeContextMenu();
      selectImage(image);

      contextMenu = document.createElement("div");
      contextMenu.className = "ql-image-context-menu";

      [
        ["left", "Align left"],
        ["center", "Align center"],
        ["right", "Align right"],
      ].forEach(([alignment, label]) => {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = label;

        button.addEventListener("pointerdown", (menuEvent) => {
          menuEvent.preventDefault();
          menuEvent.stopPropagation();

          applyImageAlignment(image, alignment);

          const block = image.closest("p, div, h1, h2, h3, h4, h5, h6, li");
          if (block) {
            block.classList.remove("ql-align-left", "ql-align-center", "ql-align-right");
            if (alignment !== "left") {
              block.classList.add(`ql-align-${alignment}`);
            }
          }

          onChange?.(quill.root.innerHTML);
          setSelectedImage(image);
          setImageWidth(String(Math.round(image.getBoundingClientRect().width)));
          updateOverlay();
          removeContextMenu();
        });

        contextMenu.appendChild(button);
      });

      wrapper.appendChild(contextMenu);

      const wrapperRect = wrapper.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();

      const menuWidth = 150;
      const menuHeight = 125;
      const left = Math.max(
        4,
        Math.min(
          event.clientX - wrapperRect.left + wrapper.scrollLeft,
          wrapper.clientWidth - menuWidth - 4
        )
      );
      const top = Math.max(
        4,
        Math.min(
          event.clientY - wrapperRect.top + wrapper.scrollTop,
          wrapper.clientHeight - menuHeight - 4
        )
      );

      contextMenu.style.left = `${left}px`;
      contextMenu.style.top = `${top}px`;
    };

    const selectImage = (image) => {
      if (!image) return;

      if (selectedImage && selectedImage !== image) {
        selectedImage.classList.remove("ql-image-selected");
      }

      selectedImage = image;
      selectedImage.classList.add("ql-image-selected");
      setSelectedImage(image);
      setImageWidth(String(Math.round(image.getBoundingClientRect().width)));
      updateOverlay();
    };

    const getImageAlignment = (image) => {
      const block = image?.closest("p, div, h1, h2, h3, h4, h5, h6, li");

      if (block) {
        const value = (block.style.textAlign || "").toLowerCase();
        if (value === "center" || value === "right") return value;
      }

      const marginLeft = image?.style.marginLeft || "";
      const marginRight = image?.style.marginRight || "";

      if (marginLeft === "auto" && marginRight === "0px") return "right";
      if (marginLeft === "auto" && marginRight === "auto") return "center";
      return "left";
    };

    const resizeImage = (event) => {
      if (!dragState || !selectedImage) return;

      const {
        startX,
        startY,
        startWidth,
        startHeight,
        position,
      } = dragState;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      const aspectRatio =
        startHeight > 0 ? startWidth / startHeight : 1;

      let width = startWidth;

      const horizontal = position.includes("e") || position.includes("w");
      const vertical = position.includes("n") || position.includes("s");

      if (horizontal) {
        width = position.includes("w")
          ? startWidth - dx
          : startWidth + dx;
      } else if (vertical) {
        const height = position.includes("n")
          ? startHeight - dy
          : startHeight + dy;

        width = height * aspectRatio;
      }

      const maxWidth = Math.min(
        IMAGE_RESIZE_MAX_WIDTH,
        editor.clientWidth - 20
      );

      width = Math.max(
        IMAGE_RESIZE_MIN_WIDTH,
        Math.min(maxWidth, width)
      );

      const height = width / aspectRatio;

      selectedImage.style.width = `${Math.round(width)}px`;
      selectedImage.style.height = `${Math.round(height)}px`;

      updateOverlay();
    };

    const persistImageChange = () => {
      if (!selectedImage) return;
      selectedImage.setAttribute(
        "width",
        String(Math.round(selectedImage.getBoundingClientRect().width))
      );
      selectedImage.setAttribute(
        "height",
        String(Math.round(selectedImage.getBoundingClientRect().height))
      );
      // Force ReactQuill to receive the edited HTML.
      onChange?.(quill.root.innerHTML);
    };

    const stopResize = () => {
      if (!dragState) return;

      dragState = null;
      document.removeEventListener("pointermove", resizeImage);
      document.removeEventListener("pointerup", stopResize);
      document.removeEventListener("pointercancel", stopResize);

      if (!selectedImage || !selectedImage.isConnected) return;

      const width = Math.round(selectedImage.getBoundingClientRect().width);
      const height = Math.round(selectedImage.getBoundingClientRect().height);

      // Persist the dimensions directly on the image element.
      // Do NOT call quill.getIndex(Quill.find(...)) here:
      // the DOM image is not guaranteed to have a registered Quill blot,
      // which causes "Cannot read properties of null (reading 'offset')".
      selectedImage.setAttribute("width", String(width));
      selectedImage.setAttribute("height", String(height));

      setImageWidth(String(width));

      // Send the edited HTML back to ReactQuill.
      // The inline width/height and attributes are preserved in the value.
      onChange?.(quill.root.innerHTML);
    };

    const startResize = (event) => {
      const handle = event.target.closest(".ql-image-handle");
      if (!handle || !selectedImage) return;

      event.preventDefault();
      event.stopPropagation();

      dragState = {
        startX: event.clientX,
        startY: event.clientY,
        startWidth: selectedImage.getBoundingClientRect().width,
        startHeight: selectedImage.getBoundingClientRect().height,
        position: handle.dataset.position,
      };

      document.addEventListener("pointermove", resizeImage);
      document.addEventListener("pointerup", stopResize);
      document.addEventListener("pointercancel", stopResize);
    };

    const getImageFromEvent = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return null;

      const image = target.closest("img");
      if (!image || !editor.contains(image)) return null;

      return image;
    };

    // Capture pointerdown before Quill handles the image embed so one click
    // reliably selects the image and shows the resize handles.
    const handleImagePointerDown = (event) => {
      const image = getImageFromEvent(event);

      if (!image) {
        if (!event.target.closest?.(".ql-image-resizer")) {
          removeOverlay();
        }
        return;
      }

      selectImage(image);
      event.stopPropagation();
    };

    const handleClick = (event) => {
      const image = getImageFromEvent(event);

      if (!image) {
        if (!event.target.closest?.(".ql-image-resizer")) {
          removeOverlay();
        }
        return;
      }

      selectImage(image);
    };

    const handleContextMenu = (event) => {
      const image = getImageFromEvent(event);
      if (!image) return;

      event.preventDefault();
      event.stopPropagation();
      showImageContextMenu(image, event);
    };

    const handleDoubleClick = (event) => {
      const image = event.target.closest("img");
      if (!image || !editor.contains(image)) return;

      const currentWidth = image.getBoundingClientRect().width;
      const naturalWidth = image.naturalWidth || currentWidth;

      image.style.width = `${Math.min(naturalWidth, editor.clientWidth - 20)}px`;
      image.style.height = "auto";

      selectImage(image);
    };

    const handleScrollOrResize = () => {
      if (selectedImage) updateOverlay();
    };

    const handleTextChange = () => {
      if (selectedImage && !selectedImage.isConnected) {
        removeOverlay();
      } else if (selectedImage) {
        updateOverlay();
      }
    };

    editor.addEventListener("pointerdown", handleImagePointerDown, true);
    editor.addEventListener("click", handleClick);
    editor.addEventListener("contextmenu", handleContextMenu);
    editor.addEventListener("dblclick", handleDoubleClick);
    overlay?.addEventListener("pointerdown", startResize);
    wrapper.addEventListener("scroll", handleScrollOrResize);
    window.addEventListener("resize", handleScrollOrResize);
    quill.on("text-change", handleTextChange);

    cleanupRef.current = () => {
      dragState = null;
      document.removeEventListener("pointermove", resizeImage);
      document.removeEventListener("pointerup", stopResize);
      editor.removeEventListener("pointerdown", handleImagePointerDown, true);
      editor.removeEventListener("click", handleClick);
      editor.removeEventListener("contextmenu", handleContextMenu);
      editor.removeEventListener("dblclick", handleDoubleClick);
      wrapper.removeEventListener("scroll", handleScrollOrResize);
      window.removeEventListener("resize", handleScrollOrResize);
      quill.off("text-change", handleTextChange);
      removeContextMenu();
      removeOverlay();
    };

    // Overlay is created lazily, so delegate pointerdown from wrapper.
    const delegatedPointerDown = (event) => {
      if (event.target.closest(".ql-image-handle")) {
        startResize(event);
      }
    };

    wrapper.addEventListener("pointerdown", delegatedPointerDown);

    return () => {
      wrapper.removeEventListener("pointerdown", delegatedPointerDown);
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [quillRef, wrapperRef, onChange, setSelectedImage, setImageWidth]);

  return null;
};

const RichTextEditor = ({ value, onChange }) => {
  const wrapperRef = useRef(null);
  const quillRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageWidth, setImageWidth] = useState("");

  /* =========================================================
     PASTE HANDLING (capture phase, before Quill's own handler)
  ========================================================= */

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;

    const handlePaste = (event) => {
      const quill = quillRef.current?.getEditor?.();
      if (!quill) return;

      const clipboardData = event.clipboardData;
      if (!clipboardData) return;

      const html = clipboardData.getData("text/html");
      const text = clipboardData.getData("text/plain");

      /* --- 1. Direct image paste (screenshot) --- */
      if (!html && clipboardData.items) {
        const items = Array.from(clipboardData.items);

        const imageItem = items.find(
          (item) => item.kind === "file" && item.type.startsWith("image/")
        );

        if (imageItem) {
          const file = imageItem.getAsFile();

          if (file) {
            event.preventDefault();
            event.stopPropagation();

            const reader = new FileReader();

            reader.onload = () => {
              const range = quill.getSelection(true) || {
                index: quill.getLength(),
                length: 0,
              };

              quill.insertEmbed(range.index, "image", reader.result, "user");
              quill.setSelection(range.index + 1, 0, "silent");
            };

            reader.readAsDataURL(file);
            return;
          }
        }
      }

      /* --- 2. Plain text — let Quill handle it --- */
      if (!html) return;

      /* --- 3. HTML — full control --- */
      event.preventDefault();
      event.stopPropagation();

      const cleaned = cleanWordHtml(html);

      const range = quill.getSelection(true) || {
        index: quill.getLength(),
        length: 0,
      };

      if (range.length > 0) {
        quill.deleteText(range.index, range.length, "user");
      }

      const hasContent =
        /<(img|hr|iframe|video|audio)\b/i.test(cleaned) ||
        cleaned
          .replace(/<[^>]*>/g, "")
          .replace(/&nbsp;/g, " ")
          .trim().length > 0;

      if (!hasContent) {
        if (text) {
          quill.insertText(range.index, text, "user");
          quill.setSelection(range.index + text.length, 0, "silent");
        }
        return;
      }

      const lengthBefore = quill.getLength();

      quill.clipboard.dangerouslyPasteHTML(range.index, cleaned, "user");

      const lengthAfter = quill.getLength();

      quill.setSelection(
        range.index + Math.max(0, lengthAfter - lengthBefore),
        0,
        "silent"
      );
    };

    wrapper.addEventListener("paste", handlePaste, true);

    return () => {
      wrapper.removeEventListener("paste", handlePaste, true);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="
        blog-quill
        relative
        overflow-hidden
        rounded-xl
        border
        border-zinc-700
        bg-zinc-900
        shadow-lg
        shadow-black/20
      "
    >
      {/* Quill dark theme — injected once, no external file */}
      <style>{QUILL_DARK_STYLES}</style>

      <Suspense
        fallback={
          <div
            className="
              flex
              min-h-[380px]
              items-center
              justify-center
              gap-2
              text-zinc-500
            "
          >
            <Loader2 className="animate-spin" size={20} />
            Loading editor…
          </div>
        }
      >
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ""}
          onChange={onChange}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          placeholder="Write your blog content here…"
        />
      </Suspense>

      {selectedImage && (
        <div className="flex flex-wrap items-center gap-2 border-t border-zinc-800 bg-zinc-900 px-3 py-2">
          <span className="text-xs font-medium text-zinc-400">Image size:</span>

          {[25, 50, 75, 100].map((percent) => (
            <button
              key={percent}
              type="button"
              className="rounded-md border border-zinc-700 px-2.5 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white"
              onClick={() => {
                const quill = quillRef.current?.getEditor?.();
                const image = wrapperRef.current?.querySelector(
                  ".ql-editor img.ql-image-selected"
                );
                if (!quill || !image) return;

                const editorWidth = Math.max(40, quill.root.clientWidth - 40);
                const width = Math.round(editorWidth * (percent / 100));
                image.style.width = `${width}px`;
                image.style.height = "auto";
                image.setAttribute("width", String(width));
                image.removeAttribute("height");
                setImageWidth(String(width));
                onChange?.(quill.root.innerHTML);
              }}
            >
              {percent}%
            </button>
          ))}

          <div className="flex items-center gap-1">
            <input
              type="number"
              min="40"
              max="2000"
              value={imageWidth}
              onChange={(e) => setImageWidth(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                const quill = quillRef.current?.getEditor?.();
                const image = wrapperRef.current?.querySelector(
                  ".ql-editor img.ql-image-selected"
                );
                const width = Number(e.currentTarget.value);
                if (!quill || !image || !Number.isFinite(width)) return;

                const safeWidth = Math.max(
                  40,
                  Math.min(2000, width)
                );
                image.style.width = `${safeWidth}px`;
                image.style.height = "auto";
                image.setAttribute("width", String(safeWidth));
                image.removeAttribute("height");
                setImageWidth(String(safeWidth));
                onChange?.(quill.root.innerHTML);
              }}
              className="w-20 rounded-md border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-xs text-white outline-none focus:border-indigo-500"
            />
            <span className="text-xs text-zinc-500">px</span>
          </div>

          <span className="mx-1 h-5 w-px bg-zinc-800" />

          <span className="text-xs font-medium text-zinc-400">Align:</span>
          {["left", "center", "right"].map((alignment) => (
            <button
              key={alignment}
              type="button"
              className={`rounded-md border px-2.5 py-1.5 text-xs transition ${(() => {
                  const image = wrapperRef.current?.querySelector(".ql-editor img.ql-image-selected");
                  const block = image?.closest("p, div, h1, h2, h3, h4, h5, h6, li");
                  const current = block?.style.textAlign || "left";
                  return current === alignment
                    ? "border-indigo-500 bg-indigo-500/20 text-indigo-300"
                    : "border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white";
                })()
                }`}
              onClick={() => {
                const quill = quillRef.current?.getEditor?.();
                const image = wrapperRef.current?.querySelector(
                  ".ql-editor img.ql-image-selected"
                );
                if (!quill || !image) return;

                applyImageAlignment(image, alignment);

                const block = image.closest("p, div, h1, h2, h3, h4, h5, h6, li");
                if (block) {
                  block.classList.remove("ql-align-left", "ql-align-center", "ql-align-right");
                  if (alignment !== "left") {
                    block.classList.add(`ql-align-${alignment}`);
                  }
                }

                onChange?.(quill.root.innerHTML);
              }}
            >
              {alignment.charAt(0).toUpperCase() + alignment.slice(1)}
            </button>
          ))}
        </div>
      )}

      <QuillImageResizer
        quillRef={quillRef}
        wrapperRef={wrapperRef}
        onChange={onChange}
        setSelectedImage={setSelectedImage}
        setImageWidth={setImageWidth}
      />
    </div>
  );
};

/* =========================================================
   EMPTY FORM
========================================================= */

const emptyForm = {
  title: "",
  excerpt: "",
  content: "",
  authorName: "",
  authorTitle: "",
  category: "",
  categoryLabel: "",
  coverImage: "",
  gradient: "",
  isActive: true,
};

/* =========================================================
   BLOG ADMIN
========================================================= */

const BlogAdmin = ({ notify }) => {
  const [blogs, setBlogs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [movingId, setMovingId] = useState(null);

  const [modalMode, setModalMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [formData, setFormData] = useState(emptyForm);

  /* =========================================================
     LOAD BLOGS
  ========================================================= */

  const fetchBlogs = async () => {
    try {
      setLoading(true);

      const res = await getBlogs();

      setBlogs(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load blogs:", err);

      notify?.(
        "error",
        err?.response?.data?.message || "Failed to load blogs"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  /* =========================================================
     FORM FIELD
  ========================================================= */

  const setField = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /* =========================================================
     IMAGE → BASE64
  ========================================================= */

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      notify?.("error", "Please select a valid image");

      event.target.value = "";
      return;
    }

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      notify?.("error", "Image size must be less than 5 MB");

      event.target.value = "";
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      setField("coverImage", reader.result);
    };

    reader.onerror = () => {
      notify?.("error", "Failed to read image");
    };

    reader.readAsDataURL(file);
  };

  /* =========================================================
     CREATE
  ========================================================= */

  const openCreateModal = () => {
    setModalMode("create");

    setEditingId(null);

    setFormData({
      ...emptyForm,
    });

    setShowModal(true);
  };

  /* =========================================================
     EDIT
  ========================================================= */

  const openEditModal = (blog) => {
    setModalMode("edit");

    setEditingId(blog.encryptedId || blog.id);

    setFormData({
      title: blog.title || "",
      excerpt: blog.excerpt || "",
      content: blog.content || "",
      authorName: blog.authorName || "",
      authorTitle: blog.authorTitle || "",
      category: blog.category || "",
      categoryLabel: blog.categoryLabel || "",
      coverImage: blog.coverImage || "",
      gradient: blog.gradient || "",
      isActive: blog.isActive !== false,
    });

    setShowModal(true);
  };

  /* =========================================================
     CLOSE MODAL
  ========================================================= */

  const closeModal = () => {
    if (saving) return;

    setShowModal(false);

    setEditingId(null);

    setFormData({
      ...emptyForm,
    });
  };

  /* =========================================================
     SAVE BLOG
  ========================================================= */

  const handleSave = async () => {
    if (saving) return;

    if (!formData.title.trim()) {
      notify?.("error", "Blog title is required");
      return;
    }

    const plainContent = formData.content
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();

    if (!plainContent) {
      notify?.("error", "Blog content is required");
      return;
    }

    if (!formData.authorName.trim()) {
      notify?.("error", "Author name is required");
      return;
    }

    if (!formData.category.trim()) {
      notify?.("error", "Category is required");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        title: formData.title.trim(),
        excerpt: formData.excerpt.trim(),
        content: formData.content,
        authorName: formData.authorName.trim(),
        authorTitle: formData.authorTitle.trim(),
        category: formData.category.trim(),
        categoryLabel: formData.categoryLabel.trim(),
        coverImage: formData.coverImage,
        gradient: formData.gradient.trim(),
        isActive: formData.isActive,
      };

      if (modalMode === "edit") {
        await updateBlog(editingId, payload);

        notify?.("success", "Blog updated successfully");
      } else {
        await createBlog(payload);

        notify?.("success", "Blog created successfully");
      }

      closeModal();

      await fetchBlogs();
    } catch (err) {
      console.error("Failed to save blog:", err);

      notify?.(
        "error",
        err?.response?.data?.message || "Failed to save blog"
      );
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     DELETE
  ========================================================= */

  const handleDelete = async (id) => {
    if (deletingId) return;

    const confirmed = window.confirm(
      "Delete this blog? This action cannot be undone."
    );

    if (!confirmed) return;

    setDeletingId(id);

    try {
      await deleteBlog(id);

      notify?.("success", "Blog deleted successfully");

      await fetchBlogs();
    } catch (err) {
      console.error("Failed to delete blog:", err);

      notify?.(
        "error",
        err?.response?.data?.message || "Failed to delete blog"
      );
    } finally {
      setDeletingId(null);
    }
  };

  /* =========================================================
     MOVE UP
  ========================================================= */

  const handleMoveUp = async (id) => {
    if (movingId) return;

    setMovingId(id);

    try {
      await moveBlogUp(id);

      await fetchBlogs();
    } catch (err) {
      console.error("Failed to move blog up:", err);

      notify?.("error", "Failed to move blog");
    } finally {
      setMovingId(null);
    }
  };

  /* =========================================================
     MOVE DOWN
  ========================================================= */

  const handleMoveDown = async (id) => {
    if (movingId) return;

    setMovingId(id);

    try {
      await moveBlogDown(id);

      await fetchBlogs();
    } catch (err) {
      console.error("Failed to move blog down:", err);

      notify?.("error", "Failed to move blog");
    } finally {
      setMovingId(null);
    }
  };

  /* =========================================================
     INPUT CLASS
  ========================================================= */

  const inputClass = `
    w-full
    rounded-lg
    border
    border-zinc-700
    bg-zinc-800
    px-3
    py-2.5
    text-sm
    text-white
    outline-none
    placeholder:text-zinc-500
    focus:border-indigo-500
  `;

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6">
      {/* HEADER */}

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-indigo-500" />
          Blog Manager
        </h1>

        <button
          onClick={openCreateModal}
          className="
            flex
            items-center
            gap-2
            bg-indigo-600
            hover:bg-indigo-500
            px-4
            py-2
            rounded-lg
            font-medium
            transition
          "
        >
          <Plus size={17} />
          Add Blog
        </button>
      </div>

      {/* LOADING / EMPTY / LIST */}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-zinc-400">
          <Loader2 className="animate-spin mr-2" size={22} />
          Loading blogs...
        </div>
      ) : blogs.length === 0 ? (
        <div
          className="
            text-center
            py-24
            text-zinc-500
            border
            border-dashed
            border-zinc-800
            rounded-xl
          "
        >
          No blogs yet.
          <br />
          Click "Add Blog" to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {blogs.map((blog, index) => {
            const blogId = blog.encryptedId || blog.id;

            return (
              <div
                key={blogId}
                className="
                  bg-zinc-900
                  border
                  border-zinc-800
                  rounded-xl
                  overflow-hidden
                "
              >
                {/* IMAGE */}

                <div className="h-40 bg-zinc-800">
                  {blog.coverImage ? (
                    <img
                      src={blog.coverImage}
                      alt={blog.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className={`
                        w-full
                        h-full
                        flex
                        items-center
                        justify-center
                        ${blog.gradient ||
                        "bg-gradient-to-br from-indigo-600 to-purple-700"
                        }
                      `}
                    >
                      <ImageIcon size={36} className="text-white/40" />
                    </div>
                  )}
                </div>

                {/* CONTENT */}

                <div className="p-5">
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className="
                        text-[10px]
                        uppercase
                        tracking-wider
                        font-bold
                        text-indigo-400
                      "
                    >
                      {blog.categoryLabel || blog.category}
                    </span>

                    <span
                      className={`
                        text-[10px]
                        px-2
                        py-1
                        rounded-full
                        ${blog.isActive
                          ? "bg-emerald-500/10 text-emerald-400"
                          : "bg-zinc-700 text-zinc-400"
                        }
                      `}
                    >
                      {blog.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <h3 className="mt-3 text-lg font-semibold text-white line-clamp-2">
                    {blog.title}
                  </h3>

                  <p className="mt-2 text-sm text-zinc-400 line-clamp-3">
                    {blog.excerpt}
                  </p>

                  <div className="mt-4">
                    <p className="text-sm text-zinc-300 font-medium">
                      {blog.authorName}
                    </p>

                    {blog.authorTitle && (
                      <p className="text-xs text-zinc-500">
                        {blog.authorTitle}
                      </p>
                    )}
                  </div>

                  {blog.date && (
                    <p className="mt-3 text-xs text-zinc-500">
                      {new Date(blog.date).toLocaleString("en-IN", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </p>
                  )}

                  {/* ACTIONS */}

                  <div
                    className="
                      flex
                      items-center
                      justify-between
                      mt-5
                      pt-4
                      border-t
                      border-zinc-800
                    "
                  >
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleMoveUp(blogId)}
                        disabled={index === 0 || movingId === blogId}
                        className="
                          p-2
                          rounded-lg
                          hover:bg-zinc-800
                          disabled:opacity-30
                        "
                        title="Move up"
                      >
                        <ArrowUp size={16} />
                      </button>

                      <button
                        onClick={() => handleMoveDown(blogId)}
                        disabled={
                          index === blogs.length - 1 || movingId === blogId
                        }
                        className="
                          p-2
                          rounded-lg
                          hover:bg-zinc-800
                          disabled:opacity-30
                        "
                        title="Move down"
                      >
                        <ArrowDown size={16} />
                      </button>
                    </div>

                    <div className="flex gap-1">
                      <button
                        onClick={() => openEditModal(blog)}
                        className="
                          p-2
                          rounded-lg
                          text-indigo-400
                          hover:bg-indigo-500/10
                        "
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>

                      <button
                        onClick={() => handleDelete(blogId)}
                        disabled={deletingId === blogId}
                        className="
                          p-2
                          rounded-lg
                          text-red-400
                          hover:bg-red-500/10
                          disabled:opacity-40
                        "
                        title="Delete"
                      >
                        {deletingId === blogId ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL */}

      {showModal && (
        <div
          className="
            fixed
            inset-0
            z-50
            bg-black/70
            backdrop-blur-sm
            flex
            items-center
            justify-center
            p-4
          "
        >
          <div
            className="
              w-full
              max-w-3xl
              max-h-[90vh]
              overflow-y-auto
              bg-zinc-900
              border
              border-zinc-800
              rounded-2xl
              shadow-2xl
            "
          >
            {/* MODAL HEADER */}

            <div
              className="
                sticky
                top-0
                z-10
                flex
                items-center
                justify-between
                px-6
                py-4
                bg-zinc-900
                border-b
                border-zinc-800
              "
            >
              <h2 className="text-lg font-semibold">
                {modalMode === "edit" ? "Edit Blog" : "Add Blog"}
              </h2>

              <button
                onClick={closeModal}
                disabled={saving}
                className="
                  p-2
                  rounded-lg
                  hover:bg-zinc-800
                  disabled:opacity-40
                "
              >
                <X size={18} />
              </button>
            </div>

            {/* FORM */}

            <div className="p-6 space-y-5">
              {/* TITLE */}

              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  Blog Title *
                </label>

                <input
                  value={formData.title}
                  onChange={(e) => setField("title", e.target.value)}
                  placeholder="Blog title"
                  className={inputClass}
                />
              </div>

              {/* EXCERPT */}

              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  Excerpt
                </label>

                <textarea
                  rows={3}
                  value={formData.excerpt}
                  onChange={(e) => setField("excerpt", e.target.value)}
                  placeholder="Short description..."
                  className={inputClass}
                />
              </div>

              {/* CONTENT */}

              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  Content *
                </label>

                <RichTextEditor
                  value={formData.content}
                  onChange={(value) => setField("content", value)}
                />
              </div>

              {/* AUTHOR */}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-300 mb-2">
                    Author Name *
                  </label>

                  <input
                    value={formData.authorName}
                    onChange={(e) => setField("authorName", e.target.value)}
                    placeholder="Vinayak V T"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="block text-sm text-zinc-300 mb-2">
                    Author Title
                  </label>

                  <input
                    value={formData.authorTitle}
                    onChange={(e) => setField("authorTitle", e.target.value)}
                    placeholder="Software Engineer"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* CATEGORY */}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-300 mb-2">
                    Category *
                  </label>

                  <input
                    value={formData.category}
                    onChange={(e) => setField("category", e.target.value)}
                    placeholder="development"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="block text-sm text-zinc-300 mb-2">
                    Category Label
                  </label>

                  <input
                    value={formData.categoryLabel}
                    onChange={(e) => setField("categoryLabel", e.target.value)}
                    placeholder="Development"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* COVER IMAGE */}

              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  Cover Image
                </label>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className={inputClass}
                />

                <p className="mt-2 text-xs text-zinc-500">
                  Upload an image. Maximum size: 5 MB.
                </p>

                {formData.coverImage && (
                  <div className="mt-4">
                    <div
                      className="
                        relative
                        overflow-hidden
                        rounded-xl
                        border
                        border-zinc-800
                        bg-zinc-950
                      "
                    >
                      <img
                        src={formData.coverImage}
                        alt="Cover preview"
                        className="w-full h-56 object-cover"
                      />

                      <button
                        type="button"
                        onClick={() => setField("coverImage", "")}
                        className="
                          absolute
                          top-3
                          right-3
                          p-2
                          rounded-lg
                          bg-black/70
                          text-white
                          hover:bg-red-500
                          transition
                        "
                        title="Remove image"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* GRADIENT */}

              <div>
                <label className="block text-sm text-zinc-300 mb-2">
                  Gradient
                </label>

                <input
                  value={formData.gradient}
                  onChange={(e) => setField("gradient", e.target.value)}
                  placeholder="bg-gradient-to-br from-indigo-600 to-purple-700"
                  className={inputClass}
                />
              </div>

              {/* ACTIVE */}

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setField("isActive", e.target.checked)}
                  className="w-4 h-4"
                />

                <span className="text-sm text-zinc-300">Active Blog</span>
              </label>

              {/* DATE INFO */}

              <div
                className="
                  rounded-lg
                  border
                  border-zinc-800
                  bg-zinc-950
                  px-4
                  py-3
                  text-xs
                  text-zinc-500
                "
              >
                {modalMode === "create"
                  ? "Date and time will automatically be set to the current date and time when this blog is created."
                  : "Date and time will automatically update to the current date and time when this blog is updated."}
              </div>
            </div>

            {/* MODAL FOOTER */}

            <div
              className="
                flex
                justify-end
                gap-3
                px-6
                py-4
                border-t
                border-zinc-800
              "
            >
              <button
                onClick={closeModal}
                disabled={saving}
                className="
                  px-4
                  py-2
                  rounded-lg
                  bg-zinc-800
                  hover:bg-zinc-700
                  text-sm
                  disabled:opacity-40
                "
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                disabled={saving}
                className="
                  flex
                  items-center
                  gap-2
                  px-5
                  py-2
                  rounded-lg
                  bg-indigo-600
                  hover:bg-indigo-500
                  disabled:opacity-50
                  text-sm
                  font-medium
                "
              >
                {saving && <Loader2 size={16} className="animate-spin" />}

                {modalMode === "edit" ? "Update Blog" : "Create Blog"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogAdmin;